import { useCallback, useMemo, useRef, useState } from 'react'
import { useMediaQuery, useTheme } from '@material-ui/core'
import useFieldFocus from '@v4/hooks/useFieldFocus'
import useEditorMeta from '@v4/hooks/useEditorMeta'
import { useSectionId } from '@v4/components/common/SectionItem'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import useRepeatableMetaData from '@v4/hooks/useRepeatableMetaData'
import { breakpointValues } from '@v4/utils/theme'
import { SLIDES_TO_SHOW } from './Video6.const'
import useStyles from './Video6.styled'

export const useVideo6 = props => {
	const { metaData } = props
	const sectionId = useSectionId()
	const theme = useTheme()
	const isTablet = useMediaQuery(theme.breakpoints.between('xs', 'sm'))
	const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
	const sliderRef = useRef(null)
	const classes = useStyles(props)

	const group = 'items'

	const items = PageBuilderSelector.useRepeatableGroupKeys({
		sectionId,
		group,
	})

	const activeItemKey = PageBuilderSelector.useMetaActiveItemKey({
		sectionId,
		group,
	})

	const activeItemIndex = useMemo(() => {
		const index = _.findIndex(items, item => item === activeItemKey)

		return index !== -1 ? index : 0
	}, [activeItemKey, items])

	const itemMetaData = useRepeatableMetaData({ group, index: activeItemIndex })

	const { handleFocus: handleTitleClick } = useFieldFocus({ metaData: metaData.title })
	const { editable: titleEditable, handleChange: handleTitleChange } = useEditorMeta({
		metaData: metaData.title,
		key: 'title',
	})

	const [currentSlide, setCurrentSlide] = useState(0)

	const handlePrevClick = useCallback(() => {
		sliderRef.current?.slickPrev()
	}, [])

	const handleNextClick = useCallback(() => {
		sliderRef.current?.slickNext()
	}, [])

	const sliderConfig = useMemo(
		() => ({
			className: classes.slider,
			dots: false,
			infinite: false,
			speed: 500,
			slidesToShow: SLIDES_TO_SHOW.LG,
			slidesToScroll: 1,
			initialSlide: 0,
			arrows: false,
			beforeChange: (current, next) => setCurrentSlide(next),
			responsive: [
				{
					breakpoint: breakpointValues.md,
					settings: {
						slidesToShow: SLIDES_TO_SHOW.MD,
						slidesToScroll: 1,
						initialSlide: 0,
						centerMode: false,
					},
				},
				{
					breakpoint: breakpointValues.sm,
					settings: {
						slidesToShow: SLIDES_TO_SHOW.SM,
						slidesToScroll: 1,
						initialSlide: 0,
						centerMode: false,
						adaptiveHeight: false,
						swipeToSlide: true,
						touchMove: true,
					},
				},
			],
		}),
		[classes.slider],
	)

	const size = _.size(items)
	const slidesToShow = isTablet ? SLIDES_TO_SHOW.MD : SLIDES_TO_SHOW.LG

	return [
		{
			classes,
			video: itemMetaData.itemVideo,
			items,
			sliderConfig,
			sliderRef,
			activeItemKey,
			showLeftButton: !isMobile && size > slidesToShow && currentSlide > 0,
			showRightButton: !isMobile && size > slidesToShow && currentSlide < size - slidesToShow,
			mobileAlignment: metaData.mobileAlignment?.metaValue,

			title: metaData.title?.metaValue,
			titleConfig: metaData.title?.config,
			titleKey: metaData.title?.tierKey,
			titleEditable,
		},
		{ handleNextClick, handlePrevClick, handleTitleClick, handleTitleChange },
	]
}
